import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, Navigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, LineChart, Bot, ListOrdered, Wallet, ArrowDownToLine, ArrowUpFromLine,
  Receipt, History, Bell, Settings, Shield, LifeBuoy, LogOut, Menu, X, ShieldCheck,
  Users, CircleDollarSign, SlidersHorizontal, FileClock, Landmark, Coins, Plug,
} from 'lucide-react';
import { Logo } from './Logo';
import { ThemeToggle, LanguageSelector } from './Controls';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';

function Spinner() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="w-10 h-10 rounded-full border-2 border-vj-neon/30 border-t-vj-neon animate-spin" />
    </div>
  );
}

export function ProtectedRoute({ admin = false }) {
  const { user, checked, isAdmin } = useAuth();
  if (!checked || user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !isAdmin) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function Shell({ items, title, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-background vj-grid-bg flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 border-r border-border bg-vj-charcoal/95 backdrop-blur-xl flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="h-16 flex items-center px-5 border-b border-border">
          <Logo size={32} />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setOpen(false)}
              data-testid={`side-${it.to.split('/').pop() || 'root'}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive ? 'bg-vj-neon/10 text-vj-neon border border-vj-neon/30' : 'text-vj-silver/70 hover:text-foreground hover:bg-white/5'
                }`
              }
            >
              <it.icon size={17} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="logout-btn" className="m-3 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-vj-red/80 hover:bg-vj-red/10 transition-colors">
          <LogOut size={17} /> Log Out
        </button>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between px-4 lg:px-8 border-b border-border bg-vj-obsidian/70 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button className="lg:hidden w-9 h-9 grid place-items-center text-foreground" onClick={() => setOpen(true)} data-testid="sidebar-toggle">
              <Menu size={18} />
            </button>
            <h1 className="font-display text-base font-semibold tracking-wide text-foreground">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
            <div className="hidden sm:flex items-center gap-2 pl-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-vj-gold to-vj-neon grid place-items-center text-vj-obsidian text-xs font-bold">
                {(user?.name || user?.email || '?').slice(0, 1).toUpperCase()}
              </div>
              <span className="text-sm text-foreground hidden md:inline max-w-[140px] truncate">{user?.name || user?.email}</span>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">{children || <Outlet />}</main>
      </div>
    </div>
  );
}

export function DashboardLayout() {
  const { t } = useI18n();
  const { isAdmin } = useAuth();
  const items = [
    { to: '/dashboard', label: t('dash.overview'), icon: LayoutDashboard, end: true },
    { to: '/dashboard/markets', label: t('nav.markets'), icon: LineChart },
    { to: '/dashboard/trading', label: t('dash.trading'), icon: CircleDollarSign },
    { to: '/dashboard/si-bot', label: t('nav.sibot'), icon: Bot },
    { to: '/dashboard/orders', label: t('dash.orders'), icon: ListOrdered },
    { to: '/dashboard/wallet', label: t('dash.wallet'), icon: Wallet },
    { to: '/dashboard/deposits', label: t('dash.deposits'), icon: ArrowDownToLine },
    { to: '/dashboard/withdrawals', label: t('dash.withdrawals'), icon: ArrowUpFromLine },
    { to: '/dashboard/settlements', label: t('dash.settlements'), icon: Receipt },
    { to: '/dashboard/history', label: t('dash.history'), icon: History },
    { to: '/dashboard/notifications', label: t('dash.notifications'), icon: Bell },
    { to: '/dashboard/settings', label: t('dash.settings'), icon: Settings },
    { to: '/dashboard/security', label: t('dash.security'), icon: Shield },
    { to: '/dashboard/support', label: t('nav.support'), icon: LifeBuoy },
  ];
  if (isAdmin) items.push({ to: '/admin', label: t('nav.admin'), icon: ShieldCheck });
  return <Shell items={items} title="VEYJEX" />;
}

export function AdminLayout() {
  const items = [
    { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/deposits', label: 'Deposits', icon: ArrowDownToLine },
    { to: '/admin/allocations', label: 'Allocations', icon: Coins },
    { to: '/admin/withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
    { to: '/admin/settlements', label: 'Settlements', icon: Receipt },
    { to: '/admin/automation', label: 'Automation', icon: SlidersHorizontal },
    { to: '/admin/trading', label: 'Trading Ops', icon: LineChart },
    { to: '/admin/exchange', label: 'Exchange', icon: Plug },
    { to: '/admin/audit-logs', label: 'Audit Logs', icon: FileClock },
    { to: '/dashboard', label: 'User Dashboard', icon: Landmark },
  ];
  return <Shell items={items} title="VEYJEX ADMIN" />;
}
